// OP1WebFrameWindow (PILOT, "frame" approach): a window that owns BEHAVIOUR
// only - drag, resize, focus, minimize/close state, and keeping its header
// inside the OP1WebWindowManagerSurface. It has almost no look of its own.
// The look is designed in Plasmic Studio through:
//   - the `header` and body (`children`) slots - drop anything in them,
//   - `headerClass` / `bodyClass` style targets, restyleable in the style
//     panel (with a "window focused" variant),
//   - the instance's own style panel (size, border, shadow, radius...).
//
// This is a sibling of OP1WebAppWindow (which is left untouched); the two can
// be compared side by side. Both read the same Desktop state.
import React, { useCallback, useEffect, useRef } from "react";
import { usePlasmicCanvasContext } from "@plasmicapp/host";
import { useDesktopState, useWindowState } from "./DesktopStateProvider";
import windowRegistry from "./windowRegistry";
import { OP1WebFrameWindowProvider } from "./OP1WebFrameWindowContext";
import OP1WebFrameWindowControl from "./OP1WebFrameWindowControl";
import styles from "./OP1WebFrameWindow.module.css";

const MIN_WIDTH = 240;
const MIN_HEIGHT = 160;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function mergeRefs(...refs) {
  return (node) => {
    refs.forEach((r) => {
      if (typeof r === "function") r(node);
      else if (r) r.current = node;
    });
  };
}

function getSurface(windowEl) {
  if (!windowEl) return null;
  return windowEl.closest("[data-window-manager-surface]") || windowEl.offsetParent;
}

// Keeps the whole header box inside the surface (the body may hang outside).
function clampHeader({ x, y, width, headerBottom, surfaceWidth, surfaceHeight }) {
  const maxX = Math.max(0, surfaceWidth - width);
  const maxY = Math.max(0, surfaceHeight - headerBottom);
  return { x: clamp(x, 0, maxX), y: clamp(y, 0, maxY) };
}

// Anything interactive a designer puts in the header must keep working
// instead of starting a drag.
const NO_DRAG = "button, a, input, textarea, select, [data-no-drag]";

const OP1WebFrameWindow = React.forwardRef(function OP1WebFrameWindow(
  { windowId, title, className, headerClass, bodyClass, header, children },
  ref
) {
  const { actions } = useDesktopState();
  const win = useWindowState(windowId);
  const dragState = useRef(null);
  const resizeState = useRef(null);
  const windowEl = useRef(null);
  const headerEl = useRef(null);
  const winRef = useRef(win);
  winRef.current = win;
  const hasWin = !!win;
  // In Studio's design canvas, always render open so content can be edited.
  const inCanvas = usePlasmicCanvasContext();

  const handleFocus = useCallback(() => {
    if (windowId) actions.focusWindow(windowId);
  }, [actions, windowId]);

  const measure = useCallback(() => {
    const surface = getSurface(windowEl.current);
    const h = headerEl.current;
    return {
      surfaceWidth: surface ? surface.clientWidth : 0,
      surfaceHeight: surface ? surface.clientHeight : 0,
      // offsetTop includes the window's border: it's the header's BOTTOM
      // edge that must stay inside the surface.
      headerBottom: h ? h.offsetTop + h.offsetHeight : 0,
    };
  }, []);

  // Re-clamp when the surface changes size (and once on mount).
  useEffect(() => {
    if (!windowId || inCanvas) return undefined;
    const surface = getSurface(windowEl.current);
    if (!surface || typeof ResizeObserver === "undefined") return undefined;

    const reclamp = () => {
      const current = winRef.current;
      if (!current) return;
      const { surfaceWidth, surfaceHeight, headerBottom } = measure();
      if (!surfaceWidth || !surfaceHeight) return;
      const next = clampHeader({
        x: current.x,
        y: current.y,
        width: current.width,
        headerBottom,
        surfaceWidth,
        surfaceHeight,
      });
      if (next.x !== current.x || next.y !== current.y) {
        actions.moveWindow(windowId, next.x, next.y);
      }
    };

    const observer = new ResizeObserver(reclamp);
    observer.observe(surface);
    return () => observer.disconnect();
  }, [actions, windowId, inCanvas, measure, hasWin]);

  const handleHeaderPointerDown = useCallback(
    (event) => {
      if (!win || !windowId) return;
      if (event.target.closest(NO_DRAG)) return;
      handleFocus();
      dragState.current = {
        startX: event.clientX,
        startY: event.clientY,
        originX: win.x,
        originY: win.y,
        width: win.width,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [win, windowId, handleFocus]
  );

  const handleHeaderPointerMove = useCallback(
    (event) => {
      if (!dragState.current || !windowId) return;
      const dx = event.clientX - dragState.current.startX;
      const dy = event.clientY - dragState.current.startY;
      const { surfaceWidth, surfaceHeight, headerBottom } = measure();
      const next =
        surfaceWidth && surfaceHeight
          ? clampHeader({
              x: dragState.current.originX + dx,
              y: dragState.current.originY + dy,
              width: dragState.current.width,
              headerBottom,
              surfaceWidth,
              surfaceHeight,
            })
          : {
              x: Math.max(0, dragState.current.originX + dx),
              y: Math.max(0, dragState.current.originY + dy),
            };
      actions.moveWindow(windowId, next.x, next.y);
    },
    [actions, windowId, measure]
  );

  const endDrag = useCallback((event) => {
    dragState.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // pointer capture may already be released
    }
  }, []);

  const handleResizePointerDown = useCallback(
    (event) => {
      if (!win || !windowId) return;
      event.stopPropagation();
      handleFocus();
      resizeState.current = {
        startX: event.clientX,
        startY: event.clientY,
        originWidth: win.width,
        originHeight: win.height,
        x: win.x,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [win, windowId, handleFocus]
  );

  const handleResizePointerMove = useCallback(
    (event) => {
      if (!resizeState.current || !windowId) return;
      const dx = event.clientX - resizeState.current.startX;
      const dy = event.clientY - resizeState.current.startY;
      const { surfaceWidth } = measure();
      // Width can't pass the surface's right edge, or the header would leave.
      const maxWidth = surfaceWidth
        ? Math.max(MIN_WIDTH, surfaceWidth - resizeState.current.x)
        : Infinity;
      actions.resizeWindow(
        windowId,
        clamp(resizeState.current.originWidth + dx, MIN_WIDTH, maxWidth),
        Math.max(MIN_HEIGHT, resizeState.current.originHeight + dy)
      );
    },
    [actions, windowId, measure]
  );

  const endResize = useCallback((event) => {
    resizeState.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // pointer capture may already be released
    }
  }, []);

  const mergedRef = useCallback((node) => mergeRefs(windowEl, ref)(node), [ref]);

  if (!windowId) {
    return (
      <div ref={mergedRef} className={[styles.frame, className].filter(Boolean).join(" ")}>
        <div className={styles.placeholder}>
          Set a &quot;Window id&quot; prop (e.g. &quot;framePilot&quot;)
        </div>
      </div>
    );
  }

  if (!win) return null;

  const isVisible = inCanvas ? true : win.isOpen && !win.isMinimized;
  const displayTitle = title || windowRegistry[windowId]?.title || windowId;
  // An empty header slot falls back to a usable default (title + controls)
  // so the window can always be dragged and closed straight out of the box.
  const headerContent =
    header != null ? (
      header
    ) : (
      <>
        <span className={styles.defaultTitle}>{displayTitle}</span>
        <span className={styles.defaultControls}>
          <OP1WebFrameWindowControl action="minimize" />
          <OP1WebFrameWindowControl action="close" />
        </span>
      </>
    );

  return (
    <OP1WebFrameWindowProvider value={{ windowId }}>
      <div
        ref={mergedRef}
        data-focused={win.isFocused ? "true" : "false"}
        className={[styles.frame, className].filter(Boolean).join(" ")}
        style={{
          display: "flex",
          visibility: isVisible ? "visible" : "hidden",
          left: win.x,
          top: win.y,
          width: win.width,
          height: win.height,
          zIndex: win.zIndex,
        }}
        onPointerDownCapture={handleFocus}
      >
        <div
          ref={headerEl}
          className={[styles.header, headerClass].filter(Boolean).join(" ")}
          onPointerDown={handleHeaderPointerDown}
          onPointerMove={handleHeaderPointerMove}
          onPointerUp={endDrag}
        >
          {headerContent}
        </div>
        <div className={[styles.body, bodyClass].filter(Boolean).join(" ")}>
          {children}
        </div>
        <div
          className={styles.resizeHandle}
          onPointerDown={handleResizePointerDown}
          onPointerMove={handleResizePointerMove}
          onPointerUp={endResize}
        />
      </div>
    </OP1WebFrameWindowProvider>
  );
});

export default OP1WebFrameWindow;
